import { useState, useEffect, useRef } from "react";
import { openExternal } from "../utils/openExternal";
import { useAuth } from "../context/AuthContext";
import { db } from "../services/firebase";
import { auth } from "../services/firebase";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { httpsCallable, FunctionsError } from "firebase/functions";
import { functions } from "../services/firebase";
import { User, Target, Pill, LogOut, Save, Trash2, Download, Bot, Droplet, HeartPulse } from "lucide-react";
import { validateWeight } from "../utils/weightValidation";
import { currentBuildInfo, formatBuildLabel } from "../utils/buildInfo";
import { trackEvent } from "../services/analytics";
import { THERAPY_REASON_OPTIONS } from "../data/therapyReasonOptions";
import type { TherapyReasonId } from "../data/therapyReasonOptions";

interface ProfilData {
  gyogyszer: string;
  kezdoSuly: string;
  celSuly: string;
  // Tárolt, de 2026-10-14 óta sem nem olvasott, sem nem írt mező (a nem létező
  // injekció-emlékeztető kapcsolója rejtve) — a meglévő értékek érintetlenek.
  emlekeztetoBe?: boolean;
  terapiaKezdete: string;
  aiEnabled?: boolean;
  glucoseEnabled?: boolean;
  therapyReason?: TherapyReasonId | null;
  injectionIntervalDays?: number | null;
  injectionAnchorDate?: string | null;
}

export default function Profil() {
  const { user, signOut, reauthenticate } = useAuth();
  const [loading, setLoading] = useState(true);
  // A2 (külső audit, 2026-10-14): sikertelen betöltés után az űrlapon az
  // alapértékek állnak (aiEnabled = true, üres kezdősúly), és a mentés
  // payloadja MINDEN mezőt explicit kiír — a `merge: true` ezért nem véd.
  // Betöltési hiba esetén a mentés tilos, amíg egy újrapróbálás nem sikerül.
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  // Búcsú-képernyő: a sikeres törlés után ez marad a képernyőn, amíg a
  // kijelentkezés át nem viszi a felhasználót a bejelentkező felületre.
  const [deleted, setDeleted] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthError, setReauthError] = useState<string | null>(null);

  const isPasswordUser = user?.providerData[0]?.providerId === "password";
  const isGoogleUser = user?.providerData[0]?.providerId === "google.com";

  // A tényleges törlés (entries + profiles + usage + Auth-user) a
  // deleteMyAccount callable-ben fut, Admin SDK-val, egy atomi szerveroldali
  // művelet része — a kliens csak elindítja, sorrend-függő félbemaradás
  // (adat törölve, Auth-user megmaradva, vagy fordítva) itt már nem lehetséges.
  const performDelete = async () => {
    const deleteMyAccount = httpsCallable(functions, "deleteMyAccount");
    await deleteMyAccount();

    // 2026-09-15 (QA D7, ClickUp 123qmzqybjz) — EZ NEM UX-KOZMETIKA.
    //
    // A szerver törli az adatot és az Auth-usert, de a kliensben megmarad az
    // auth.currentUser, és a már kiadott ID-token a lejáratáig (~1 óra)
    // ÉRVÉNYES: a Firestore szabályok az aláírást és a claimeket nézik, nem
    // azt, hogy a felhasználó létezik-e még. A szerveroldali
    // revokeRefreshTokens ezen nem segít — az csak új token váltását
    // akadályozza meg.
    //
    // Ami emiatt történt: a törlés után az app az onboardingra esett vissza
    // (nincs profil → nincs onboardingDone), és a „Kihagyom, később
    // beállítom" gomb (Onboarding.tsx handleComplete) visszaírt egy
    // profiles/{uid} dokumentumot egy már nem létező uid-re — ÁRVA PROFIL az
    // éles adatbázisban. A napi invariáns-ellenőrzés `orphan_profiles`
    // tétele ezt már detektálja.
    //
    // Az azonnali kijelentkezés zárja le a hibaosztályt: a profiles/{uid}
    // mindhárom írója (Onboarding handleComplete, Profil mentés, Fooldal
    // horgony-javaslat) felhasználói műveletre indul, és kijelentkezve
    // egyikhez sem lehet eljutni.
    setDeleted(true);
    await signOut();
  };

  // A szerver (functions/src/index.ts) az ID-token auth_time claimjéből saját
  // maga is kikényszeríti a friss bejelentkezést — ez a kliensoldali
  // előellenőrzés csak UX-optimalizálás (elkerüli a felesleges round-tripet),
  // nem az egyetlen védelmi vonal.
  const handleDeleteAccount = async () => {
    if (!user || !auth.currentUser) return;
    setDeleting(true);
    setReauthError(null);
    try {
      const tokenResult = await auth.currentUser.getIdTokenResult();
      const staleMs = Date.now() - new Date(tokenResult.authTime).getTime();
      if (staleMs > 4 * 60 * 1000) {
        setNeedsReauth(true);
        setDeleting(false);
        return;
      }
      await performDelete();
    } catch (err: any) {
      if (err instanceof FunctionsError && err.code === "functions/failed-precondition") {
        setNeedsReauth(true);
        setDeleting(false);
        return;
      }
      alert("Hiba történt a törlés során. Kérlek próbáld újra.");
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleReauthAndDelete = async () => {
    setDeleting(true);
    setReauthError(null);
    try {
      await reauthenticate(isPasswordUser ? reauthPassword : undefined);
      await performDelete();
      setNeedsReauth(false);
    } catch (err: any) {
      setReauthError(
        err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? "Helytelen jelszó."
          : "A hitelesítés sikertelen volt. Próbáld újra."
      );
      setDeleting(false);
    }
  };

  const cancelReauth = () => {
    setNeedsReauth(false);
    setDeleteConfirm(false);
    setReauthPassword("");
    setReauthError(null);
  };

  const handleExportData = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, "entries"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const entries = snap.docs.map(d => d.data());
      const profilSnap = await getDoc(doc(db, "profiles", user.uid));
      const usageSnap = await getDoc(doc(db, "usage", user.uid));
      // ── JOGI NYUGTÁK (ClickUp 123qmzqzk1r §5b/1) ────────────────────
      //
      // ⚠️ EZ NEM OPCIONÁLIS, ÉS AZ INDOK A SZIMMETRIA. Ma ugyanaz a halmaz,
      // amit a deleteMyAccount TÖRÖL, és amit az export TARTALMAZ:
      //     törlés : entries · profiles · usage · Auth
      //     export : entries · profiles · usage (+ email)
      // A `legalAcks` a cascade-be bekerül (§5, FPM döntése). Ha az exportba
      // NEM kerülne be, akkor a nyugta-nyilvántartás MAGA sértené azt a
      // hozzáférési jogot, amit védeni akar: olyan személyes adatot
      // tárolnánk, amit az érintett NEM KÉRHET KI, de amit a törléskor
      // eltüntetünk. Egy olvasás és egy mező az ára.
      //
      // A lekérdezés a saját `userId`-re szűr — a firestore.rules ezt
      // engedi (2026-10-06-án mérve, `firestore.rules.lists.test.js`), a
      // szűrés nélküli és az idegen `userId`-re szűrt listát viszont tiltja.
      const acksSnap = await getDocs(
        query(collection(db, "legalAcks"), where("userId", "==", user.uid))
      );
      const exportData = {
        exportDate: new Date().toISOString(),
        email: user.email,
        profil: profilSnap.data() || {},
        entries,
        usage: usageSnap.data() || {},
        jogiNyugtak: acksSnap.docs.map((d) => d.data()),
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `glp-naplo-adatok-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Hiba történt az export során.");
    }
  };

  // 2026-08-17: nincs kitalált alapérték — ha a mező üres, itt is üres
  // marad, amíg a user ténylegesen nem választ. Lásd docs/FOGALMAK.md #9.
  const [gyogyszer, setGyogyszer] = useState("");
  const [kezdoSuly, setKezdoSuly] = useState("");
  const [celSuly, setCelSuly] = useState("");
  const [terapiaKezdete, setTerapiaKezdete] = useState("");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [glucoseEnabled, setGlucoseEnabled] = useState(false);
  const [therapyReason, setTherapyReason] = useState<TherapyReasonId | null>(null);
  // #86cb5dbkn: opcionális korrekció, üresen a mai (naplózásból derivált)
  // viselkedés változatlan — lásd docs/FOGALMAK.md, src/utils/therapyStats.ts.
  const [injectionIntervalDays, setInjectionIntervalDays] = useState("");
  const [injectionAnchorDate, setInjectionAnchorDate] = useState("");
  // A betöltéskori érték — csak ehhez képest ténylegesen megváltozott
  // értéknél tüzelünk analytics-eseményt mentéskor (nem minden kattintásnál).
  const glucoseEnabledLoadedRef = useRef(false);
  const therapyReasonLoadedRef = useRef<TherapyReasonId | null>(null);

  // Kezdősúly opcionális ezen az oldalon (a felhasználó már beállította
  // onboardingnál) — üresen hagyva menthető, de ha van érték, érvényesnek
  // kell lennie.
  const kezdoSulyError = kezdoSuly === "" ? null : validateWeight(kezdoSuly);
  const injectionIntervalError =
    injectionIntervalDays === "" ||
    (/^\d+$/.test(injectionIntervalDays) && Number(injectionIntervalDays) >= 1 && Number(injectionIntervalDays) <= 30)
      ? null
      : "1 és 30 közötti egész szám legyen";

  // Betöltés Firestore-ból
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadError(false);
      try {
        const ref = doc(db, "profiles", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data() as ProfilData;
          setGyogyszer(d.gyogyszer || "");
          setKezdoSuly(d.kezdoSuly || "");
          setCelSuly(d.celSuly || "");
          setTerapiaKezdete(d.terapiaKezdete || "");
          setAiEnabled(d.aiEnabled ?? true);
          setGlucoseEnabled(d.glucoseEnabled ?? false);
          glucoseEnabledLoadedRef.current = d.glucoseEnabled ?? false;
          setTherapyReason(d.therapyReason ?? null);
          therapyReasonLoadedRef.current = d.therapyReason ?? null;
          setInjectionIntervalDays(d.injectionIntervalDays != null ? String(d.injectionIntervalDays) : "");
          setInjectionAnchorDate(d.injectionAnchorDate || "");
        }
      } catch (err) {
        console.error("Profil betöltési hiba:", err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, loadAttempt]);

  const handleSave = async () => {
    if (!user || loadError || kezdoSulyError || injectionIntervalError) return;
    setSaving(true);
    setSaveError(false);
    try {
      // merge: true KÖTELEZŐ — merge nélkül a setDoc a teljes dokumentumot
      // cseréli, és minden mező, ami nincs ebben a payloadban (pl.
      // onboardingDone, therapyReason), törlődne. 2026-08-14: pontosan ez
      // okozta, hogy meglévő felhasználók a mentés után újra onboardingot
      // láttak, és az AI-asszisztens kikapcsolása is csendben visszaállt.
      await setDoc(doc(db, "profiles", user.uid), {
        gyogyszer,
        kezdoSuly,
        celSuly,
        terapiaKezdete,
        aiEnabled,
        glucoseEnabled,
        therapyReason,
        injectionIntervalDays: injectionIntervalDays === "" ? null : Number(injectionIntervalDays),
        injectionAnchorDate: injectionAnchorDate === "" ? null : injectionAnchorDate,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      if (glucoseEnabled !== glucoseEnabledLoadedRef.current) {
        trackEvent(glucoseEnabled ? "glucose_logging_enabled" : "glucose_logging_disabled");
        glucoseEnabledLoadedRef.current = glucoseEnabled;
      }
      // Szándékosan NEM kapcsoljuk automatikusan a glucoseEnabled-et, ha a
      // therapyReason cukorbetegségre változik — az onboarding auto-pipálása
      // csak első-beállításkori kényelmi funkció volt ("bármikor módosíthatod
      // a Profil oldalon"), a glucoseEnabled itt már önállóan szerkeszthető,
      // a user tudatos döntését nem írjuk felül mellékhatásként.
      if (therapyReason !== therapyReasonLoadedRef.current) {
        trackEvent("therapy_reason_set", { source: "profil" });
        therapyReasonLoadedRef.current = therapyReason;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error("Mentési hiba:", err);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Búcsú-képernyő — a sikeres törlés és a kijelentkezés közötti pillanatban
  // ez látszik. Fontos, hogy NE a profil vagy az onboarding maradjon a
  // képernyőn: a QA D7 tétele pont azt találta meg, hogy a törlés után
  // bentragadt felület visszaírta a profilt (ClickUp 123qmzqybjz).
  if (deleted) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-20 pb-6 text-center space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">A fiókod törölve lett</h1>
        <p className="text-sm text-gray-500 leading-relaxed">
          Minden adatod — a bejegyzéseid, a profilod és a beállításaid — véglegesen
          törlődött. Köszönjük, hogy használtad a GLP Naplót.
        </p>
        <p className="text-xs text-gray-400">
          Kijelentkeztetünk…
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 pb-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profil</h1>
        <p className="text-sm text-gray-500 mt-0.5">Személyes beállítások</p>
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
          <p className="text-sm font-semibold text-red-700">Nem sikerült betölteni a profilodat</p>
          <p className="text-xs text-red-600">
            Ellenőrizd az internetkapcsolatot, és próbáld újra. Amíg a betöltés nem sikerül,
            a mentés ki van kapcsolva, hogy a beállításaid ne íródjanak felül.
          </p>
          <button
            onClick={() => {
              setLoading(true);
              setLoadAttempt((n) => n + 1);
            }}
            className="w-full border border-red-200 text-red-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors"
          >
            Újrapróbálom
          </button>
        </div>
      )}

      {/* Avatar */}
      <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
          <User size={28} className="text-emerald-700" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">{user?.email}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {user?.emailVerified ? "✅ Email megerősítve" : "⚠️ Email nincs megerősítve"}
          </p>
        </div>
      </div>

      {/* Gyógyszer */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex items-center gap-2">
          <Pill size={15} className="text-emerald-700" />
          <h2 className="text-sm font-semibold text-gray-700">Gyógyszer</h2>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {["tirzepatide", "semaglutide"].map((g) => (
            <button
              key={g}
              onClick={() => setGyogyszer(g)}
              className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all ${
                gyogyszer === g
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"
              }`}
            >
              {g === "tirzepatide" ? "Tirzepatide" : "Semaglutide"}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">
          {gyogyszer === "tirzepatide"
            ? "Mounjaro / Zepbound — GLP-1 + GIP kettős agonista"
            : gyogyszer === "semaglutide"
            ? "Ozempic / Wegovy — GLP-1 receptor agonista"
            : "Válassz egy készítményt — ez alapján mutatjuk a helyes klinikai összehasonlítást."}
        </p>
      </div>

      {/* Terápia kezdete */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Target size={15} className="text-emerald-700" />
          <h2 className="text-sm font-semibold text-gray-700">Terápia adatok</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Terápia kezdete</label>
            <input
              type="date"
              value={terapiaKezdete}
              onChange={(e) => setTerapiaKezdete(e.target.value)}
              className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Kezdő súly (kg)</label>
              <input
                type="number"
                value={kezdoSuly}
                onChange={(e) => setKezdoSuly(e.target.value)}
                placeholder="pl. 125"
                className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              {kezdoSulyError && <p className="mt-1 text-xs text-red-600">{kezdoSulyError}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Célsúly (kg)</label>
              <input
                type="number"
                value={celSuly}
                onChange={(e) => setCelSuly(e.target.value)}
                placeholder="pl. 90"
                className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </div>
          {/* #86cb5dbkn: opcionális korrekció a "Következő injekció"
              kártyához — ha üres, a naplózásból derivált dátum/7 napos
              ciklus marad érvényben, nincs változás senkinek. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Injekciós ciklus hossza (nap) — opcionális</label>
              <input
                type="number"
                min={1}
                max={30}
                value={injectionIntervalDays}
                onChange={(e) => setInjectionIntervalDays(e.target.value)}
                placeholder="alapértelmezett: 7"
                className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              {injectionIntervalError && <p className="mt-1 text-xs text-red-600">{injectionIntervalError}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Legutóbbi injekció dátuma — opcionális</label>
              <input
                type="date"
                value={injectionAnchorDate}
                onChange={(e) => setInjectionAnchorDate(e.target.value)}
                className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Terápia oka — közös forrás Onboarding.tsx-szel: src/data/therapyReasonOptions.ts */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex items-center gap-2">
          <HeartPulse size={15} className="text-emerald-700" />
          <h2 className="text-sm font-semibold text-gray-700">Miért kaptad a terápiát?</h2>
        </div>
        <div className="space-y-2">
          {THERAPY_REASON_OPTIONS.map((r) => (
            <button
              key={r.id}
              onClick={() => setTherapyReason(r.id)}
              className={`w-full text-left py-2.5 px-3 rounded-xl text-sm font-medium border transition-all ${
                therapyReason === r.id
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">
          Ez határozza meg, melyik klinikai vizsgálattal hasonlítjuk össze a haladásodat a Statisztika és a Főoldal grafikonján.
        </p>
      </div>

      {/* Az injekció-emlékeztető kapcsolója 2026-10-14 óta REJTVE (FPM döntése):
          a funkció nem létezik — az `emlekeztetoBe` mezőt semmi nem olvasta,
          értesítés nem ment ki. Visszahozni a funkcióval együtt (ClickUp
          123qmzqzpm5), a featurePromises.test.ts tudatos módosításával. */}

      {/* AI asszisztens */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot size={15} className="text-emerald-700" />
            <p className="text-sm font-semibold text-gray-700">AI asszisztens</p>
          </div>
          <button
            onClick={() => setAiEnabled(!aiEnabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              aiEnabled ? "bg-emerald-600" : "bg-gray-200"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                aiEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Az asszisztens a kérdéseidet és a naplóadataidat (súly, dózis, terápia hossza) az Anthropic
          rendszerében dolgozza fel. Kikapcsolva nem küldünk adatot, és az asszisztens nem érhető el.
        </p>
      </div>

      {/* Vércukor naplózása */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Droplet size={15} className="text-emerald-700" />
            <p className="text-sm font-semibold text-gray-700">Vércukor naplózása</p>
          </div>
          <button
            onClick={() => setGlucoseEnabled(!glucoseEnabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              glucoseEnabled ? "bg-emerald-600" : "bg-gray-200"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                glucoseEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Ha cukorbetegként méred az éhomi vércukrodat, itt bekapcsolhatod a naplózását. A bejegyzéseidnél
          megjelenik egy mező, és a statisztikákban külön görbén látod az értékeket.
        </p>
      </div>

      {/* Mentés */}
      <button
        onClick={handleSave}
        disabled={saving || loadError || !!kezdoSulyError}
        className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving ? (
          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
        ) : saved ? (
          "✅ Mentve!"
        ) : (
          <><Save size={16} /> Beállítások mentése</>
        )}
      </button>
      {saveError && (
        <p className="text-xs text-red-600 text-center">
          Nem sikerült menteni. Ellenőrizd az internetkapcsolatot, és próbáld újra.
        </p>
      )}

      {/* Ko-fi */}
      <button
        type="button"
        onClick={() => openExternal("https://ko-fi.com/glpnaplo")}
        className="w-full flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 py-3 rounded-xl font-semibold transition-colors"
      >
        ☕ Támogasd a fejlesztést
      </button>

      {/* Kijelentkezés */}
      <button
        onClick={signOut}
        className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
      >
        <LogOut size={16} />
        Kijelentkezés
      </button>

      {/* Adatok letöltése */}
      <button
        onClick={handleExportData}
        className="w-full flex items-center justify-center gap-2 border border-blue-200 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
      >
        <Download size={16} />
        Adataim letöltése (JSON)
      </button>

      {/* Fiók törlése */}
      {!deleteConfirm ? (
        <button
          onClick={() => setDeleteConfirm(true)}
          className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 py-3 rounded-xl font-semibold hover:bg-red-50 transition-colors"
        >
          <Trash2 size={16} />
          Fiók törlése
        </button>
      ) : needsReauth ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
          <p className="text-sm font-semibold text-red-700">Erősítsd meg újra a belépésed</p>
          <p className="text-xs text-red-600">
            Biztonsági okokból friss bejelentkezés szükséges a fiók végleges törléséhez.
          </p>
          {isPasswordUser && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Jelszó</label>
              <input
                type="password"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
          )}
          {reauthError && <p className="text-xs text-red-600">{reauthError}</p>}
          <div className="flex gap-2">
            <button
              onClick={cancelReauth}
              className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Mégsem
            </button>
            <button
              onClick={handleReauthAndDelete}
              disabled={deleting || (isPasswordUser && !reauthPassword)}
              className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {deleting
                ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                : isPasswordUser
                ? "Megerősítés és törlés"
                : `Újra hitelesítés (${isGoogleUser ? "Google" : "Apple"}) és törlés`}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
          <p className="text-sm font-semibold text-red-700">Biztosan törlöd a fiókodat?</p>
          <p className="text-xs text-red-600">Ez a művelet visszafordíthatatlan. Minden bejegyzésed és adatod véglegesen törlődik.</p>
          <div className="flex gap-2">
            <button
              onClick={() => setDeleteConfirm(false)}
              className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Mégsem
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {deleting ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : "Igen, törlöm"}
            </button>
          </div>
        </div>
      )}

      {/* Verzió + build-azonosító. A build-címke a `buildInfo` KANONIKUS
          függvényéből jön (CLAUDE.md 10. szabály) — itt ne fogalmazzuk újra.
          Miért látszik a usernek: natív appban ez az EGYETLEN hely, ahol
          kiderül, melyik commit webbuildje fut — a becsomagolt bundle nincs
          a verziókövetésben, egy Store-os kliens pedig a beküldés napján
          befagy. Egy hibabejelentésnél ez a sor dönti el, mit kérdezzünk. */}
      <p className="text-center text-xs text-gray-300">
        GLP Napló v1.0 · {formatBuildLabel(currentBuildInfo())}
      </p>
    </div>
  );
}
